import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useGameState } from './hooks/useGameState';
import { useDiscordIntegration } from './hooks/useDiscordIntegration';
import StartScreen from './components/screens/StartScreen';
import GameScreen from './components/screens/GameScreen';
import PictureGameScreen from './components/screens/PictureGameScreen';
import ResultsScreen from './components/screens/ResultsScreen';
import TermsOfService from './components/screens/TermsOfService';
import PrivacyPolicy from './components/screens/PrivacyPolicy';
import OldGameBanner from './components/common/OldGameBanner';
import Footer from './components/layout/Footer';

function GameApp() {
  const {
    state,
    startGame,
    startPictureGame,
    makeSelection,
    makePictureSelection,
    getAccuracy,
    isLoading,
    clearOldGameAndStartFresh,
    continueChallengeAfterPopup,
    finishGameFromPopup,
    resetToMenu,
    resumeGame,
    hasSavedTextProgress,
    hasSavedPictureProgress,
  } = useGameState();

  const [selectedMode, setSelectedMode] = useState<'text' | 'picture' | null>(null);

  // Discord integration (only active when running in Discord)
  const discord = useDiscordIntegration();

  // Update Discord activity based on game state
  useEffect(() => {
    if (!discord.isDiscordEnvironment) return;

    const updateActivity = async () => {
      if (state.gameStarted && !state.gameFinished) {
        // Game in progress
        await discord.updateActivity({
          details: `Playing ${selectedMode === 'picture' ? 'Picture' : 'Text'} Guessing Game`,
          state: `Round ${state.currentRound}/${state.totalRounds} • Score: ${state.score}`,
          largeImageKey: 'game-logo',
          largeImageText: 'AI Guessing Game',
          smallImageKey: 'playing',
          smallImageText: 'In Game',
        });
      } else if (state.gameFinished) {
        // Game completed
        const accuracy = getAccuracy();
        await discord.updateActivity({
          details: `Completed ${selectedMode === 'picture' ? 'Picture' : 'Text'} Guessing Game`,
          state: `Score: ${state.score}/${state.totalRounds} (${accuracy}% accuracy)`,
          largeImageKey: 'game-logo',
          largeImageText: 'AI Guessing Game',
          smallImageKey: 'completed',
          smallImageText: 'Game Complete',
        });
      } else {
        // Waiting to start
        await discord.updateActivity({
          details: `AI vs Human Guessing Game`,
          state: `Ready to play`,
          largeImageKey: 'game-logo',
          largeImageText: 'AI Guessing Game',
          smallImageKey: 'waiting',
          smallImageText: 'Ready to Play',
        });
      }
    };

    updateActivity().catch((error) => {
      console.error('Failed to update Discord activity in App:', error);
    });
  }, [state.gameStarted, state.gameFinished, state.currentRound, state.totalRounds, state.score, discord, getAccuracy, selectedMode]);

  const handleStartGame = (mode: 'text' | 'picture') => {
    setSelectedMode(mode);

    // Check if already played
    if (mode === 'text' && state.hasPlayedTextToday) {
      return; // renderScreen will handle showing results
    }

    if (mode === 'picture' && state.hasPlayedPictureToday) {
      return; // renderScreen will handle showing results
    }

    // Check for saved progress to resume
    if (mode === 'text' && hasSavedTextProgress) {
      resumeGame('text');
      return;
    }

    if (mode === 'picture' && hasSavedPictureProgress) {
      resumeGame('picture');
      return;
    }

    if (mode === 'text') {
      startGame();
    } else {
      startPictureGame();
    }
  };

  const handleBackToMenu = () => {
    setSelectedMode(null);
    resetToMenu();
  };

  // Render the appropriate screen based on game state
  const renderScreen = () => {
    // If no mode selected, show Main Menu (StartScreen with mode selection)
    if (!selectedMode) {
      return (
        <StartScreen
          onStartText={() => handleStartGame('text')}
          onStartPicture={() => handleStartGame('picture')}
          isLoading={isLoading}
          gameDate={state.gameDate}
          textGameStatus={{
            played: state.hasPlayedTextToday,
            score: state.textScore,
            total: state.textTotalRounds
          }}
          pictureGameStatus={{
            played: state.hasPlayedPictureToday,
            score: state.pictureScore,
            total: state.pictureTotalRounds
          }}
          hasSavedTextProgress={hasSavedTextProgress}
          hasSavedPictureProgress={hasSavedPictureProgress}
        />
      );
    }

    // If user has already played this mode today, show results screen
    // This handles both the "just finished" case and the "reloaded page" case
    const hasPlayedCurrentMode = selectedMode === 'text' ? state.hasPlayedTextToday : state.hasPlayedPictureToday;

    if (hasPlayedCurrentMode) {
      // Determine score and total rounds based on mode
      const score = selectedMode === 'text' ? state.textScore : state.pictureScore;
      const totalRounds = selectedMode === 'text' ? state.textTotalRounds : state.pictureTotalRounds;

      // Determine target score based on mode
      const targetScore = selectedMode === 'picture'
        ? (state.dailyPictureGameData?.targetScore ?? state.requiredCorrectAnswers)
        : (state.dailyGameData?.gameSet.challengeTargetScore ?? state.requiredCorrectAnswers);

      // Calculate accuracy safely
      const accuracy = totalRounds > 0 ? Math.round((score / totalRounds) * 100) : 0;

      return (
        <ResultsScreen
          mode={selectedMode}
          score={score}
          totalRounds={totalRounds}
          userSelections={state.userSelections || []} // Might be empty on reload, but that's okay for basic results
          accuracy={accuracy}
          onBackToMenu={handleBackToMenu}
          hasPlayedToday={true}
          gameDate={state.gameDate}
          requiredCorrectAnswers={targetScore}
          nextGameAvailableAt={state.nextGameAvailableAt}
          releaseScheduleLabel={state.releaseScheduleLabel}
          releaseTimeZone={state.releaseScheduleTimeZone}
        />
      );
    }

    // If game is finished (fallback for non-daily games or edge cases)
    if (state.gameFinished) {
      return (
        <ResultsScreen
          mode={selectedMode}
          score={state.score}
          totalRounds={state.totalRounds}
          userSelections={state.userSelections}
          accuracy={getAccuracy()}
          onBackToMenu={handleBackToMenu}
          hasPlayedToday={hasPlayedCurrentMode}
          gameDate={state.gameDate}
          requiredCorrectAnswers={state.requiredCorrectAnswers}
          nextGameAvailableAt={state.nextGameAvailableAt}
          releaseScheduleLabel={state.releaseScheduleLabel}
          releaseTimeZone={state.releaseScheduleTimeZone}
        />
      );
    }

    // If game has started, show game screen
    if (state.gameStarted) {
      if (selectedMode === 'picture') {
        return (
          <PictureGameScreen
            pair={state.currentPicturePair}
            currentRound={state.currentRound}
            totalRounds={state.totalRounds}
            score={state.score}
            onMakeSelection={makePictureSelection}
            isLoading={isLoading}
            requiredCorrectAnswers={state.requiredCorrectAnswers}
            showChallengeCompletedPopup={state.showChallengeCompletedPopup}
            onContinueChallenge={continueChallengeAfterPopup}
            onFinishGame={finishGameFromPopup}
            onBackToMenu={handleBackToMenu}
          />
        );
      }

      return (
        <GameScreen
          aiText={state.currentAIText}
          humanText={state.currentHumanText}
          currentRound={state.currentRound}
          totalRounds={state.totalRounds}
          score={state.score}
          onMakeSelection={makeSelection}
          isLoading={isLoading}
          requiredCorrectAnswers={state.requiredCorrectAnswers}
          showChallengeCompletedPopup={state.showChallengeCompletedPopup}
          onContinueChallenge={continueChallengeAfterPopup}
          onFinishGame={finishGameFromPopup}
          onBackToMenu={handleBackToMenu}
        />
      );
    }

    // Fallback to menu
    return (
      <StartScreen
        onStartText={() => handleStartGame('text')}
        onStartPicture={() => handleStartGame('picture')}
        isLoading={isLoading}
        gameDate={state.gameDate}
        textGameStatus={{
          played: state.hasPlayedTextToday,
          score: state.textScore,
          total: state.textTotalRounds
        }}
        pictureGameStatus={{
          played: state.hasPlayedPictureToday,
          score: state.pictureScore,
          total: state.pictureTotalRounds
        }}
        hasSavedTextProgress={hasSavedTextProgress}
        hasSavedPictureProgress={hasSavedPictureProgress}
      />
    );
  };

  return (
    <div className="App">
      {/* Show Discord user info if available */}
      {discord.isDiscordEnvironment && discord.discordUser && (
        <div className="discord-header bg-indigo-600 text-white px-4 py-2 text-sm">
          🎮 Playing as {discord.userDisplayName} in Discord
        </div>
      )}

      {/* Show old game banner if detected */}
      {state.hasOldGame && (
        <OldGameBanner
          oldGameDate={state.oldGameDate}
          onStartLatestGame={() => clearOldGameAndStartFresh()}
          isLoading={isLoading}
        />
      )}

      {renderScreen()}
    </div>
  );
}

function App() {
  return (
    <Router>
      <div className="min-h-screen flex flex-col">
        <div className="flex-1">
          <Routes>
            <Route path="/" element={<GameApp />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          </Routes>
        </div>
        <Footer />
      </div>
    </Router>
  );
}

export default App;
